import React from 'react';
import { AppResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

/**
 * LoginBrandPanel — painel de marca (coluna esquerda) da tela de Login.
 *
 * Exclusivo do Login (o AuthBrandingPanel legado é compartilhado por
 * Register/ResetPassword e NÃO deve ser alterado). Painel fixo/flat em cor
 * sólida primária (white-label: logo/nome/cor vêm do app). Some no mobile.
 */
const FEATURES: { icon: IconName; label: string }[] = [
  { icon: 'message-circle', label: 'Multicanal em uma caixa de entrada' },
  { icon: 'zap', label: 'Agentes de IA que atendem por você' },
  { icon: 'trending-up', label: 'CRM e funis com automações' },
];

interface LoginBrandPanelProps {
  app: AppResponse | null;
  primaryColor: string;
}

export const LoginBrandPanel: React.FC<LoginBrandPanelProps> = ({ app, primaryColor }) => {
  const brandName = app?.name || 'TROIA';

  return (
    <aside
      className="relative hidden flex-col justify-between overflow-hidden px-[52px] py-12 text-white min-[860px]:flex"
      style={{ backgroundColor: primaryColor }}
    >
      {/* Círculos decorativos (flat, sutil) */}
      <div
        aria-hidden
        className="pointer-events-none absolute rounded-full"
        style={{ right: -80, bottom: -80, width: 320, height: 320, backgroundColor: 'rgba(255,255,255,0.06)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute rounded-full"
        style={{ left: -60, top: 60, width: 180, height: 180, backgroundColor: 'rgba(255,255,255,0.05)' }}
      />

      {/* Logo (topo) */}
      <div className="relative z-10 flex items-center gap-3">
        {app?.logo ? (
          <img src={app.logo} alt={brandName} className="h-10 w-auto" />
        ) : (
          <span
            className="flex h-10 w-10 items-center justify-center rounded-[11px] text-xl font-black"
            style={{ backgroundColor: '#ffffff', color: primaryColor }}
          >
            {brandName.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="text-xl font-extrabold tracking-tight">{brandName}</span>
      </div>

      {/* Conteúdo (meio) */}
      <div className="relative z-10 max-w-[420px]">
        <h2 className="text-3xl font-bold leading-tight tracking-tight">
          Atendimento, CRM e IA em um só lugar.
        </h2>
        <p className="mb-6 mt-3.5 text-[15px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
          Centralize WhatsApp, Instagram e e-mail, automatize com agentes de IA e feche mais negócios.
        </p>
        <ul className="flex flex-col gap-3.5">
          {FEATURES.map((f) => (
            <li
              key={f.label}
              className="flex items-center gap-3 text-[14.5px]"
              style={{ color: 'rgba(255,255,255,0.94)' }}
            >
              <span
                className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-lg"
                style={{ backgroundColor: 'rgba(255,255,255,0.16)' }}
              >
                <Icon name={f.icon} className="h-[15px] w-[15px]" />
              </span>
              {f.label}
            </li>
          ))}
        </ul>
      </div>

      {/* Rodapé */}
      <div className="relative z-10 text-[13px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
        © {new Date().getFullYear()} {brandName} · Todos os direitos reservados
      </div>
    </aside>
  );
};

LoginBrandPanel.displayName = 'LoginBrandPanel';
